import { ARTIFACTS_PATH } from '@studio/shared';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Previews for the two single-image Asset Types.
 *
 * Everything here is a way of looking at bytes that already exist. The Google masks and
 * the small launcher sizes are drawn by the browser over the untouched Artifact, and the
 * feature-graphic crops are the same image seen through different windows. Nothing is
 * rendered into a file and nothing is baked: the exported icon is the full square it was
 * generated as, and the exported feature graphic is the whole graphic.
 */

const MASKS: readonly { readonly name: string; readonly radius: string }[] = [
  { name: 'круг', radius: '50%' },
  { name: 'squircle', radius: '28%' },
  { name: 'скруглённый квадрат', radius: '18%' },
];

/** The sizes an icon is actually judged at, rather than the size it is stored at. */
const SMALL_SIZES: readonly { readonly label: string; readonly px: number }[] = [
  { label: 'лаунчер', px: 48 },
  { label: 'результат поиска', px: 32 },
  { label: 'уведомление', px: 24 },
];

function art(artifactId: string | null): string | null {
  return artifactId === null ? null : `${ARTIFACTS_PATH}/${artifactId}`;
}

function blank(size: number): CSSProperties {
  return { width: size, height: size, background: 'rgba(120,130,150,0.25)', display: 'inline-block' };
}

export function IconPreview({
  artifactId,
  headline,
  size = 96,
}: {
  artifactId: string | null;
  headline: string;
  size?: number;
}): ReactNode {
  const src = art(artifactId);

  return (
    <div className="icon-preview">
      <div className="icon-masks" role="group" aria-label={`Превью маски Google для ${headline}`}>
        {MASKS.map((mask) => (
          <span key={mask.name} className="icon-mask" style={{ display: 'inline-block' }}>
            {src === null ? (
              <span style={{ ...blank(size), borderRadius: mask.radius }} aria-hidden="true" />
            ) : (
              <img
                src={src}
                alt={`${headline} под маской «${mask.name}»`}
                width={size}
                height={size}
                style={{ borderRadius: mask.radius, objectFit: 'cover' }}
                loading="lazy"
              />
            )}
            <span className="note">{mask.name}</span>
          </span>
        ))}
      </div>

      <div className="icon-sizes" role="group" aria-label={`Малые размеры для ${headline}`}>
        {SMALL_SIZES.map((small) => (
          <span key={small.label} style={{ display: 'inline-block' }}>
            {src === null ? (
              <span style={{ ...blank(small.px), borderRadius: '22%' }} aria-hidden="true" />
            ) : (
              <img
                src={src}
                alt={`${headline} в размере «${small.label}»`}
                width={small.px}
                height={small.px}
                style={{ borderRadius: '22%', objectFit: 'cover' }}
                loading="lazy"
              />
            )}
            <span className="note">{small.label}</span>
          </span>
        ))}
      </div>

      <p className="note">
        Маски и малые размеры — так Google показывает иконку. Это только превью: в Export уходит полный квадрат
        без скруглённых углов и без тени, вписанной в файл.
      </p>
    </div>
  );
}

/** The windows a feature graphic is seen through. Representative, never authoritative. */
const CROPS: readonly { readonly label: string; readonly ratio: string }[] = [
  { label: 'шапка Store Listing', ratio: '1024 / 500' },
  { label: 'узкий кроп телефона', ratio: '3 / 2' },
  { label: 'обложка видео', ratio: '16 / 9' },
];

export function FeatureGraphicPreview({
  artifactId,
  headline,
  width = 240,
}: {
  artifactId: string | null;
  headline: string;
  width?: number;
}): ReactNode {
  const src = art(artifactId);

  return (
    <div className="feature-preview">
      <div className="feature-crops" role="group" aria-label={`Превью кропов и обложек для ${headline}`}>
        {CROPS.map((crop) => (
          <span key={crop.label} style={{ display: 'inline-block', width }}>
            <span
              style={{
                display: 'block',
                width: '100%',
                aspectRatio: crop.ratio,
                overflow: 'hidden',
                background: 'rgba(120,130,150,0.25)',
              }}
            >
              {src === null ? null : (
                <img
                  src={src}
                  alt={`${headline} как «${crop.label}»`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  loading="lazy"
                />
              )}
            </span>
            <span className="note">{crop.label}</span>
          </span>
        ))}
      </div>

      <p className="note">
        Эти кропы — как графика показывается в разных местах. Это только превью: в Export уходит вся
        графика, и Studio не выдумывает внутри неё защищённую зону.
      </p>
    </div>
  );
}
